import PropTypes from 'prop-types';
import { formatCurrency } from 'utils/formatCurrency';
import useLiterals from 'utils/hooks/useLiterals';
import Button from 'components/UI/Button';
import Box from 'components/UI/Box/Box';

/**
 * Componente de resumen del pago antes de procesarlo.
 * Usa literals.mercadopago.summary para todos los textos.
 */
const PaymentSummary = ({
  paymentData,
  orders = [],
  isMultiple = false,
  error = null,
  warning = null,
  loading = false,
  recommendOtherMethod = false,
  onUseOtherPaymentMethod = () => {},
  onConfirm = () => {},
  onBack = () => {},
}) => {
  const literals = useLiterals('mercadopago');
  const s = literals.summary || {};

  return (
    <Box>
      <div className='payment-summary'>
        <h4 className='mb-4'>{s.title}</h4>

        {error && (
          <div className='alert alert-danger mb-6' role='alert'>
            <strong>{s.errorTitle}</strong>
            {' '}
            {error}
          </div>
        )}

        {recommendOtherMethod && (
          <div className='alert alert-warning mb-6' role='alert'>
            <strong>{s.warningTitle}</strong>
            {' '}
            {s.recommendOtherMethodMessage || 'Recomendamos usar otro método de pago.'}
          </div>
        )}

        {warning && !error && (
          <div className='alert alert-warning mb-6' role='alert'>
            <strong>{s.warningTitle}</strong>
            {' '}
            {warning}
          </div>
        )}

        {isMultiple ? (
          <div className='mb-6'>
            <p className='mb-2 text-neutral-500 payment-summary-label'>{s.contracts}</p>
            <div className='payment-summary-contracts-list mb-4'>
              {orders.map((order, index) => (
                <div
                  key={order.contract_id ?? index}
                  className='flex justify-between items-start py-2 payment-summary-contract-item'
                >
                  <div className='grow'>
                    <p className='mb-0 font-semibold'>{order.contract_id}</p>
                    {order.payment_name && (
                      <p className='mb-0 text-sm text-neutral-500 mt-1'>{order.payment_name}</p>
                    )}
                  </div>
                  <p className='mb-0 font-semibold text-right'>
                    {formatCurrency(order.amount, 'MXN')}
                  </p>
                </div>
              ))}
            </div>
            <div className='flex justify-between items-center pt-2'>
              <p className='mb-0 font-bold'>{s.totalAmount}</p>
              <h2 className='mb-0 payment-summary-amount'>
                {formatCurrency(paymentData.amount, 'MXN')}
              </h2>
            </div>
            <div className='mb-6'>
              <p className='mb-2 text-neutral-500 payment-summary-label mt-4'>{s.holder}</p>
              <p className='mb-0 payment-summary-value'>{paymentData.cardholderName}</p>
            </div>
            <div className='mb-6'>
              <p className='mb-2 text-neutral-500 payment-summary-label'>{s.card}</p>
              <p className='mb-0 payment-summary-value'>{paymentData.cardNumber}</p>
            </div>
          </div>
        ) : (
          <>
            <div className='mb-6'>
              <p className='mb-2 text-neutral-500 payment-summary-label'>{s.amount}</p>
              <h2 className='payment-summary-amount'>
                {formatCurrency(paymentData.amount, 'MXN')}
              </h2>
            </div>

            <div className='mb-6'>
              <p className='mb-2 text-neutral-500 payment-summary-label'>{s.holder}</p>
              <p className='mb-0 payment-summary-value'>{paymentData.cardholderName}</p>
            </div>

            <div className='mb-6'>
              <p className='mb-2 text-neutral-500 payment-summary-label'>{s.card}</p>
              <p className='mb-0 payment-summary-value'>{paymentData.cardNumber}</p>
            </div>
          </>
        )}

        {paymentData.installments && paymentData.installments !== '1' && (
          <div className='mb-6'>
            <p className='mb-2 text-neutral-500 payment-summary-label'>{s.installments}</p>
            <p className='mb-0 payment-summary-value'>{paymentData.installments}</p>
          </div>
        )}

        <div className='flex flex-col-reverse md:flex-row gap-2 mt-6'>
          {recommendOtherMethod ? (
            <Button
              text={s.useOtherPaymentMethodButton || 'Volver a métodos de pago'}
              type='primary'
              className='w-full'
              onClick={onUseOtherPaymentMethod}
            />
          ) : (
            <>
              <Button
                text={s.back}
                type='secondary'
                className='flex-1'
                onClick={onBack}
              />
              <Button
                text={s.confirm}
                type='primary'
                className='flex-1'
                onClick={onConfirm}
                disabled={!!error || !!warning || loading}
              />
            </>
          )}
        </div>
      </div>
    </Box>
  );
};

PaymentSummary.propTypes = {
  paymentData: PropTypes.object.isRequired,
  orders: PropTypes.array,
  isMultiple: PropTypes.bool,
  error: PropTypes.string,
  warning: PropTypes.string,
  loading: PropTypes.bool,
  recommendOtherMethod: PropTypes.bool,
  onUseOtherPaymentMethod: PropTypes.func,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default PaymentSummary;
